import { useEffect, useRef, useState } from "react";
import { Loader2, Send } from "lucide-react";
import { useApp } from "@/context/AppContext";
import api from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { MediaPicker } from "./MediaPicker";
import { uploadMedia, validMedia } from "@/lib/uploadMedia";

export const ExperienceComposer = ({ place, onClose, onPublished }) => {
  const { t } = useApp();
  const [caption, setCaption] = useState("");
  const [placeId, setPlaceId] = useState(place?.id || "");
  const [places, setPlaces] = useState([]);
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [error, setError] = useState("");
  const controller = useRef(null);
  const liveItems = useRef(items);
  const postId = useRef(crypto.randomUUID().replaceAll("-", ""));
  liveItems.current = items;
  useEffect(() => {
    if (!place) api.get("/places").then(({ data }) => setPlaces(data)).catch(() => setError("experience_load_error"));
    return () => {
      controller.current?.abort();
      liveItems.current.forEach((item) => URL.revokeObjectURL(item.url));
    };
  }, [place]);
  const update = (key, patch) => setItems((prev) => prev.map((item) => item.key === key ? { ...item, ...patch } : item));
  const add = (files) => {
    setError("");
    if (items.length + files.length > 6) return setError("experience_too_many");
    if (files.some((file) => !validMedia(file))) return setError("experience_invalid");
    setItems((prev) => [...prev, ...files.map((file) => ({ key: crypto.randomUUID(), file, url: URL.createObjectURL(file), progress: 0 }))]);
  };
  const remove = (key) => {
    const item = items.find((entry) => entry.key === key);
    URL.revokeObjectURL(item.url);
    if (item.upload) api.delete(`/media/uploads/${item.upload.id}`).catch(() => {});
    setItems((prev) => prev.filter((entry) => entry.key !== key));
  };
  const cancel = () => {
    if (publishing) return;
    controller.current?.abort();
    liveItems.current.forEach((item) => { if (item.upload) api.delete(`/media/uploads/${item.upload.id}`).catch(() => {}); });
    onClose();
  };
  const submit = async (event) => {
    event.preventDefault();
    if (busy || !items.length) return;
    setBusy(true); setError("");
    controller.current = new AbortController();
    try {
      const mediaIds = [];
      for (const item of items) mediaIds.push(await uploadMedia(item, controller.current.signal, (patch) => update(item.key, patch)));
      setPublishing(true);
      await api.post("/experiences", { id: postId.current, caption, place_id: placeId || null, media_ids: mediaIds }, { signal: controller.current.signal });
      onPublished();
    } catch (e) {
      if (e.code !== "ERR_CANCELED") setError(e.response?.status === 503 ? "experience_unavailable" : ["media_invalid", "video_processing_failed"].includes(e.response?.data?.detail) ? "experience_processing_error" : "experience_failed");
    } finally { setBusy(false); setPublishing(false); }
  };
  return <Dialog open onOpenChange={(open) => { if (!open && !busy) cancel(); }}>
    <DialogContent data-testid="experience-composer" className="max-h-[90dvh] w-[calc(100%-2rem)] max-w-lg overflow-y-auto rounded-xl" onInteractOutside={(e) => busy && e.preventDefault()}>
      <DialogHeader><DialogTitle className="font-heading">{t("new_experience")}</DialogTitle><DialogDescription data-testid="experience-public-notice">{t("experience_public")}</DialogDescription></DialogHeader>
      <form onSubmit={submit} className="space-y-4">
        {place ? <p data-testid="experience-fixed-place" className="text-sm font-semibold text-sea">{place.name}</p> : <label className="block text-xs font-semibold text-ink">{t("experience_place")}<select data-testid="experience-place-select" value={placeId} disabled={busy} onChange={(e) => setPlaceId(e.target.value)} className="mt-1 w-full min-w-0 rounded-lg border border-border bg-white p-2.5 text-sm"><option value="">{t("experience_no_place")}</option>{places.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}</select></label>}
        <textarea data-testid="experience-caption-input" aria-label={t("experience_caption")} value={caption} disabled={busy} onChange={(e) => setCaption(e.target.value)} maxLength={2000} rows={3} placeholder={t("experience_caption")} className="w-full resize-y rounded-lg border border-border bg-white p-3 text-sm focus:border-terracotta focus:outline-none" />
        <MediaPicker items={items} busy={busy} onAdd={add} onRemove={remove} />
        {error && <p data-testid="experience-publish-error" role="alert" className="text-sm text-red-700">{t(error)}</p>}
        <div className="flex items-center gap-3">
          <button data-testid="experience-cancel" type="button" disabled={publishing} onClick={cancel} className="rounded-full px-4 py-3 text-sm font-semibold text-ink-muted disabled:opacity-50">{t("cancel")}</button>
          <button data-testid="experience-publish" type="submit" disabled={busy || !items.length} className="flex min-w-0 flex-1 items-center justify-center gap-2 rounded-full bg-terracotta px-4 py-3 text-sm font-bold text-white transition-colors hover:bg-terracotta-hover disabled:opacity-50">{busy ? <Loader2 className="h-4 w-4 shrink-0 animate-spin" /> : <Send className="h-4 w-4 shrink-0" />}<span>{busy ? t(publishing ? "experience_publishing" : "experience_uploading") : t("publish")}</span></button>
        </div>
      </form>
    </DialogContent>
  </Dialog>;
};